import _ from 'lodash';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { AxiosError } from 'axios';

export interface CustomError {
  code: number | string;
  message: string;
  request?: string;
  requestType?: string;
}

export class CustomErrorClass implements CustomError {
  code: number | string;
  message: string;
  request: string;
  requestType?: string;

  constructor(error: AxiosError) {
    this.code = error.response?.status ?? error.code  ?? 'UNKNOWN_ERROR';
    this.message = (error?.response?.data as any)?.message ?? error.message ?? 'UNKNOWN_MESSAGE';
    this.request = error.config?.url ?? 'UNKNOWN_REQUEST';
    this.requestType = error.config?.method ?? 'UNKNOWN_METHOD' as 'get' | 'post' | 'put' | 'delete' | 'UNKNOWN_METHOD';
  }
}

interface ErrorStore {
  errorOccurred: boolean;
  isNetworkError: boolean;
  errors: CustomError[];
  source: string | null;
  timeStamp: string | null;

  handleOpenErrorDialog: (newError: CustomError | undefined, newRequest: string, newRequestType?: string | undefined) => void;

  handleCloseErrorDialog: () => void;
}

const useErrorStore = createWithEqualityFn<ErrorStore>(
  (set, get) => ({
    errorOccurred: false,
    isNetworkError: false,
    errors: [],
    source: null,
    timeStamp: null,

    handleOpenErrorDialog(newError: CustomError | undefined, newRequest: string, newRequestType?: string | undefined) {
      const currentErrors = get().errors;

      const path = document.location.href;

      const currentDate = new Date();
      const currentDateISOString = currentDate.toISOString();

      const isArrayBuffer = (element: any) => {
        return element instanceof ArrayBuffer;
      };

      if (isArrayBuffer(newError) && !_.isNil(newError)) {
        newError.code = 500;
        newError.message = 'Internal Server Error (Array Buffer)';
      }

      // Unwrap nested backend error format: { success: false, error: { code, message } }
      if (
        !_.isNil(newError) &&
        _.isNil(newError.code) &&
        _.isNil(newError.message) &&
        !_.isNil((newError as any).error) &&
        (!_.isNil((newError as any).error.code) || !_.isNil((newError as any).error.message))
      ) {
        newError = (newError as any).error;
      }

      if (
        !_.isNil(newError) &&
        newError.code !== 401 &&
        newError.code !== 'ERR_NETWORK' &&
        (!_.isNil(newError.code) || !_.isNil(newError.message) || isArrayBuffer(newError)) &&
        newError.code !== 'ERR_CANCELED'
      ) {
        set(() => ({
          errorOccurred: true,
          isNetworkError: false,
          errors: [...currentErrors, { ...newError, request: newRequest, requestType: newRequestType }],
          source: path,
          timeStamp: currentDateISOString,
        }));
      } else if (!_.isNil(newError) && (newError.code === 'ERR_NETWORK' || (_.isNil(newError.code) && _.isNil(newError.message)))) {
        set(() => ({
          errorOccurred: true,
          isNetworkError: true,
          errors: [],
          source: path,
          timeStamp: currentDateISOString,
        }));
      }
    },

    handleCloseErrorDialog() {
      set(() => ({ errorOccurred: false }));

      setTimeout(() => {
        set(() => ({ errors: [], source: null, isNetworkError: false }));
      }, 225);
    },
  }),
  shallow
);

export default useErrorStore;
